import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/data/api';
import Toast from '@/utils/toastManager';
import {
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { LESSON_PLAN_LABELS, getLessonPlanDisplayName } from '@/constants/lessonPlanLabels';

const LessonPlanManagement = () => {
	const [lessonPlans, setLessonPlans] = useState([]);
	const [loading, setLoading] = useState(true);
	const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
	const navigate = useNavigate();

	useEffect(() => {
		fetchLessonPlans();
	}, []);

	const fetchLessonPlans = async () => {
		try {
			setLoading(true);
			const response = await api.getLessonPlans();
			setLessonPlans(response?.data || []);
		} catch (err) {
			Toast.error(err.message || 'Failed to fetch lesson plans');
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		try {
			await api.deleteLessonPlan(deleteDialog.id);
			Toast.success('Lesson plan deleted successfully');
			setDeleteDialog({ open: false, id: null });
			fetchLessonPlans();
		} catch (err) {
			Toast.error(err.message || 'Failed to delete lesson plan');
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ padding: '2rem' }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Typography variant="h4">{LESSON_PLAN_LABELS.PROGRESSIVE_PLURAL} Management</Typography>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={() => navigate('/AdminDashboard/add-lesson-plan')}
				>
					Add {LESSON_PLAN_LABELS.PROGRESSIVE}
				</Button>
			</Box>

			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Title</TableCell>
							<TableCell>Category</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Created At</TableCell>
							<TableCell align="center">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{lessonPlans.length === 0 ? (
							<TableRow>
								<TableCell colSpan={5} align="center">
									No lesson plans found
								</TableCell>
							</TableRow>
						) : (
							lessonPlans.map((plan) => (
								<TableRow key={plan._id}>
									<TableCell>{plan.title}</TableCell>
									<TableCell>{plan.categoriesDetails?.map(c => getLessonPlanDisplayName(c.name)).join(', ') || '-'}</TableCell>
									<TableCell>{plan.isActive ? 'Active' : 'Inactive'}</TableCell>
									<TableCell>{new Date(plan.createdAt).toLocaleDateString()}</TableCell>
									<TableCell align="center">
										<IconButton
											color="primary"
											onClick={() => navigate(`/AdminDashboard/edit-lesson-plan/${plan._id}`)}
										>
											<Edit />
										</IconButton>
										<IconButton
											color="error"
											onClick={() => setDeleteDialog({ open: true, id: plan._id })}
										>
											<Delete />
										</IconButton>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>Are you sure you want to delete this lesson plan?</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
					<Button onClick={handleDelete} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default LessonPlanManagement;
